import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import z from "zod";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type Role = "ADMIN" | "PROJECT_MANAGER";

export const roles = {
  admin: { value: "ADMIN", label: "Admin", fullLabel: "Admin" },
  pm: {
    value: "PROJECT_MANAGER",
    label: "PM",
    fullLabel: "Project Manager",
  },
  employee: { value: "EMPLOYEE", label: "Employee", fullLabel: "Employee" },
  customer: { value: "CUSTOMER", label: "Customer", fullLabel: "Customer" },
  system_admin: {
    value: "SYSTEM_ADMIN",
    label: "System Admin",
    fullLabel: "System Admin",
  },
};

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const getRequiredFields = (schema: any, prefix = ""): string[] => {
  // If current schema is ZodObject
  if (schema instanceof z.ZodObject) {
    return Object.entries(schema.shape).flatMap(([key, field]) => {
      const fieldName = prefix ? `${prefix}.${key}` : key;
      return getRequiredFields(field, fieldName);
    });
  }

  // If current schema is ZodArray – recurse inside array element type
  if (schema instanceof z.ZodArray) {
    return getRequiredFields(schema._def.type, prefix);
  }

  // For primitive required fields
  const isRequired =
    (schema instanceof z.ZodString ||
      schema instanceof z.ZodNumber ||
      schema instanceof z.ZodDate ||
      schema instanceof z.ZodBoolean) &&
    !schema.isOptional?.();

  return isRequired ? [prefix] : [];
};

export const removeEmptyFields = (data: any) => {
  return Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );
};

export const setEmptyFieldsToNull = (data: any) => {
  Object.keys(data).forEach((key) => {
    if (data[key] == "") {
      data[key] = null;
    }
  });
  return data;
};

export const capitalize = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const generateRandomUppercase = (): string => {
  return Math.random().toString(36).substring(2, 6).toUpperCase();
};

// label to display value in UI
// className for styling of text
// value to pass in API
export const projectStatus: any = {
  draft: {
    label: "Draft",
    className: "text-blue-600",
    value: "DRAFT",
  },
  planning: {
    label: "Ready for Planning",
    className: "text-amber-600",
    value: "PLANNING",
  },
  in_progress: {
    label: "In Progress",
    className: "text-amber-400",
    value: "IN_PROGRESS",
  },
  on_hold: {
    label: "On Hold",
    className: "text-yellow-800",
    value: "ON_HOLD",
  },
  completed: {
    label: "Completed",
    className: "text-green-600",
    value: "COMPLETED",
  },
  cancelled: {
    label: "Cancelled",
    className: "text-red-600",
    value: "CANCELLED",
  },
};

export const getProjectStatusLabel = (statusValue: string) => {
  const status = Object.values(projectStatus).find(
    (s: any) => s.value === statusValue
  ) as { label: string } | undefined;
  return status?.label || statusValue;
};

export const getProjectStatusClassName = (statusValue: string) => {
  const status = Object.values(projectStatus).find(
    (s: any) => s.value === statusValue
  ) as { className: string } | undefined;
  return status?.className || "";
};

export const projectHealthStatus: any = {
  on_track: {
    label: "On Track",
    value: "ON_TRACK",
    apiKey: "onTrack",
  },
  delayed: {
    label: "Delayed",
    value: "DELAYED",
    apiKey: "delayed",
  },
  critical: {
    label: "Critical",
    value: "CRITICAL",
    apiKey: "critical",
  },
};

export const getProjectHealthLabel = (healthValue: string) => {
  const status = (Object.entries(projectHealthStatus).find(
    ([key, s]: [string, any]) =>
      s.value === healthValue || s.label === healthValue || key === healthValue
  )?.[1] || {}) as { label: string } | undefined;
  return status?.label || healthValue;
};

export const getProjectHealthKey = (healthValue: string) => {
  const entry = Object.entries(projectHealthStatus).find(
    ([key, s]: [string, any]) =>
      s.value === healthValue || s.label === healthValue || key === healthValue
  );
  return entry ? entry[0] : "on_track";
};


// #0"APPROVAL_PENDING"
// #1"APPROVED"
// #2"REJECTED"
// #3"CLOSED"

// label to display value in UI
// className for styling of text
// value to pass in API
export const planStatus: any = {
  draft: {
    label: "Draft",
    className: "text-blue-600",
    value: "DRAFT",
  },
  approval_pending: {
    label: "Pending Approval",
    className: "text-amber-600",
    value: "APPROVAL_PENDING",
  },
  approved: {
    label: "Approved",
    className: "text-amber-600",
    value: "APPROVED",
  },
  revised: {
    label: "Revised",
    className: "text-red-600",
    value: "REVISE",
  },
  rejected: {
    label: "Rejected",
    className: "text-red-600",
    value: "REJECTED",
  },
  closed: {
    label: "Closed",
    className: "text-grey-600",
    value: "CLOSED",
  },
};

export const attachmentEntityTypes = {
  projectDesignDoc: 'PROJECT_DESIGN_DOC',
  task: 'TASK',
  taskQualityCheckUpdate: 'TASK_QUALITY_CHECK_UPDATE',
  taskSafetyCheckUpdate: 'TASK_SAFETY_CHECK_UPDATE',
  userDp: 'USER_DP'
}

export const dependencyTypes: any = {
  "e2s": "Finish to Start",
  "e2e": "Finish to Finish",
  "s2s": "Start to Start",
  "s2e": "Start to Finish",
}

export const importanceFlagOptions = [
  { label: "Low", value: "LOW" },
  { label: "Medium", value: "MEDIUM" },
  { label: "High", value: "HIGH" },
];

export const truncateByWords = (text: string, limit: number) => {
  if (!text) return "";
  const words = text.trim().split(/\s+/);
  
  if (words.length > limit) {
    return words.slice(0, limit).join(" ") + "...";
  }

  const charLimit = 20;
  if (text.length > charLimit) {
    return text.slice(0, charLimit) + "...";
  }

  return text;
};

export const getBaseRoute = (path: string) =>
  path?.split("/")?.filter(Boolean)?.[0] || "";

export const taskStatus: any = {
  draft: {
    label: 'Not Started',
    className: 'border-gray-400 bg-gray-100 fill-gray-100'
  }, // not started
  in_progress: {
    label: 'In Progress',
    className: 'border-amber-400 bg-amber-200 fill-amber-200'
  }, // in progress
  completed: { 
    label: 'Completed',
    className: 'border-green-600 bg-green-200 fill-green-200'
  }, // completed
  on_hold: { 
    label: 'On-Hold',
    className: 'border-brown-400 bg-brown-300 fill-brown-300'
  }, // on hold
  blocked: { 
    label: 'Blocked',
    className: 'border-red-600 bg-red-200 fill-red-200'
  }, // blocked
}